import {
  BookOpen,
  ClipboardList,
  FileText,
  FolderKanban,
  GitBranch,
  Layers,
  ListTree,
  MessageSquareX,
  Shield,
  Spline,
  UserCog,
  type LucideIcon,
} from "lucide-react";

import type { Capability } from "@/api/hooks";

/**
 * Declarative navigation model.
 *
 * Every entry here is a real destination. Issue #4's rule — no control that
 * implies working functionality — used to be met by a second `unavailable`
 * variant that rendered a non-interactive item stating what it was waiting for;
 * #516 removes the variant along with its only remaining user, because the
 * canonical product's sidebar carries two entries and no placeholder. A section
 * with nothing behind it is now absent, which is the same rule with one less
 * way to satisfy it.
 */

export interface NavItem {
  key: string;
  label: string;
  icon: LucideIcon;
  /** Where the entry navigates. Every entry navigates. */
  to: string;
  /** Hidden entirely when the session lacks this capability. */
  capability?: Capability;
}

export interface NavGroup {
  key: string;
  label: string;
  items: NavItem[];
}

/**
 * The whole primary navigation: THREE groups, THREE items — the two #516
 * ratified plus User administration, which only an `authz.manage` holder
 * sees (the third group below records why it is not the deleted `Users`).
 *
 * #516's own account of the two-item sidebar, kept as written:
 *
 * The canonical product reference's sidebar is exactly `Projects` and
 * `Clinical library`, and this application's was four entries in three groups.
 * The `System` group went with the addresses behind it:
 *
 *  * `Roles` (#265) — a real surface, and still deleted rather than hidden. Its
 *    premise is unchanged (role assignment is Editor-mastered since #130, and
 *    #264 shipped `/v1/role-assignments`), but the reference answers 404 at
 *    `/roles`, and this ticket permits exactly the visible post-login product
 *    the reference has. The API and its authorization model are untouched.
 *  * `Audit` (#153) — the last `unavailable` placeholder. A permanently
 *    disabled entry is a surface a clinician has to read past on every screen.
 *
 * `Users` and `Settings` are not here for their older reasons, both still true:
 * this product holds no user directory to enumerate, and workspace settings are
 * Tenant Manifest domain. `src/invariants/routes.test.ts` is what keeps a third
 * entry from arriving quietly — it fails on any address this table's
 * destinations do not appear in.
 */
export const NAV_GROUPS: NavGroup[] = [
  {
    key: "operations",
    label: "Operations",
    items: [
      {
        key: "projects",
        label: "Projects",
        icon: FolderKanban,
        capability: "project.read",
        to: "/projects",
      },
    ],
  },
  {
    key: "clinical-content",
    label: "Clinical content",
    items: [
      {
        key: "library",
        // #489: "Clinical library", the product name the ticket makes
        // consistent. "Clinical editor" named the whole application here and
        // the shared catalogue on `/library` at the same time, so the sidebar
        // entry read as a link to the product a clinician was already inside.
        label: "Clinical library",
        icon: BookOpen,
        capability: "library.read",
        to: "/library/pathways",
      },
    ],
  },
  {
    // The third group, beneath Clinical library: access administration. ONE
    // entry, gated on `authz.manage` — the capability the service grants to
    // `admin` alone — so every other session sees the two-item sidebar #516
    // ratified. Hiding it is presentation: the route behind it answers the
    // service's own 403 to anyone else who types the address.
    //
    // This is not the `Users` entry #153 removed: that one implied a user
    // directory the product would enumerate, and this product still holds
    // none. User administration lists the people who HOLD a grant and lets an
    // administrator pick an existing Entra identity through a bounded
    // directory search; it creates no account and enumerates no directory.
    key: "administration",
    label: "Administration",
    items: [
      {
        key: "user-administration",
        label: "User administration",
        icon: UserCog,
        capability: "authz.manage",
        to: "/user-administration",
      },
    ],
  },
];

/**
 * Section rails inside the project and library surfaces.
 *
 * `owner` names the issue that builds the section's editor. #4 establishes the
 * routes so later tickets slot in without renaming anything.
 */
export interface SectionItem {
  slug: string;
  label: string;
  /** Issue that delivers this section's authoring UI. */
  owner: string;
  /**
   * The section's own one-sentence description, shown under its heading (#908).
   *
   * DECLARED HERE rather than inside each catalog, because it is the same fact
   * the rail entry already carries — what this section holds — and because the
   * generic `LibraryCatalog` serves five sections from one component and cannot
   * hold five descriptions of its own without a second table keyed by slug.
   * `SectionSurface` reads it off the matched entry and hands it to whichever
   * body renders that section, so a section added to the rail without one is a
   * type error rather than a heading with nothing under it.
   *
   * Optional because the clinical-configuration rail has no such header: a
   * Project section is titled by the Project shell, which #909 owns.
   */
  description?: string;
  /**
   * The rail glyph, for rails that show one (#908).
   *
   * The library rail in the reference carries an icon per entry and the
   * clinical-configuration rail carries none, so this is per ITEM rather than a
   * rail-wide switch: `SectionRail` renders what an entry declares and lays out
   * correctly either way.
   */
  icon?: LucideIcon;
  /**
   * Where this rail entry's body comes from. Defaults to `"section"`.
   *
   * `"section"` means the slug is a member of the contract's `SectionSlug` and
   * the body is `getProjectSection`'s effective projection. `"none"` means the
   * entry is a destination in the rail whose body is assembled from some OTHER
   * project-scoped read, and whose slug is deliberately NOT a `SectionSlug`.
   *
   * #489 introduces the second kind for `questionnaires`. Declaring it here
   * rather than special-casing it in `SectionSurface` is what lets
   * `src/invariants/routes.test.ts` keep asserting the rail and the contract
   * agree — it checks the projection-backed entries against `SECTION_SLUGS`
   * exactly as before, and separately asserts that a `"none"` entry can never
   * BE a `SectionSlug`. A future entry that quietly dropped this marker would
   * fail that invariant rather than reaching `getProjectSection` with a slug
   * the endpoint has no section for.
   */
  projection?: "section" | "none";
}

export interface SectionGroup {
  label: string;
  items: SectionItem[];
}

/**
 * The clinical section a Project opens on.
 *
 * One constant rather than a literal in each caller: `ProjectClinicalConfigRoute`
 * passes it to `SectionSurface` as the section to resolve in place at the
 * project root (#203), and `ProjectLayoutRoute` names the same section in the
 * breadcrumb trail there (#489). Two spellings of "bg-sets" is how the header
 * and the body come to disagree about which section a clinician is looking at.
 */
export const CLINICAL_DEFAULT_SLUG = "bg-sets";

export const CLINICAL_CONFIG_SECTIONS: SectionGroup[] = [
  {
    // #1086. A group of ONE, above the content areas, because what it holds is
    // not a content area: it reads across every section below and names where
    // each piece of the project's clinical content comes from. The rail's own
    // groups are content areas (`Background`, `Interview`, `Summary`), so a
    // cross-cutting destination inside any one of them would claim to be part
    // of that area.
    //
    // "All sections" rather than "Overview", deliberately: the Project shell
    // above this rail already has an `Overview` TAB pointing somewhere else,
    // and two controls on one screen carrying one word for two destinations is
    // the repeated-ambiguous-control defect in a different costume.
    label: "All sections",
    items: [
      {
        // `projection: "none"`, like `questionnaires` (#489) and `closing`
        // (#867), and for the identical strict reason: `content-sources` is not
        // a member of the contract's `SectionSlug`, and `SectionSurface`'s guard
        // exists to stop a rail slug reaching `getProjectSection` on the
        // strength of its name. `src/invariants/routes.test.ts` asserts that
        // such an entry can never BE a contract slug, so this marker and that
        // guard fail together rather than one of them quietly widening.
        //
        // The body is #1086's Content sources surface, assembled from #1085's
        // `GET /projects/{id}/clinical-inventory` — the one read that names
        // content the project inherits with no library row and no catalog entry
        // at all, which is why it is not, and cannot be, a section projection.
        slug: "content-sources",
        label: "Content sources",
        owner: "#1086",
        projection: "none",
      },
    ],
  },
  {
    label: "Background",
    items: [{ slug: "bg-sets", label: "BG sets", owner: "#33" }],
  },
  {
    label: "Interview",
    items: [
      { slug: "chief-complaint", label: "Main concern", owner: "#33" },
      { slug: "pathways", label: "Pathways", owner: "#33" },
      { slug: "rules", label: "Rules", owner: "#33" },
      { slug: "safety", label: "Safety", owner: "#35" },
      { slug: "alerts", label: "Alerts", owner: "#35" },
      {
        // #489. An INTENTIONAL Questionnaire destination, and the reason it
        // carries `projection: "none"`: `questionnaires` is not a member of the
        // contract's `SectionSlug` (`contracts/generated/editor-service.d.ts`),
        // and `SectionSurface`'s guard exists precisely to stop it reaching
        // `getProjectSection`. The ticket asks for the destination WITHOUT
        // widening the contract, so the entry declares that its body is not a
        // section projection and `SectionSurface` renders #369's project-scoped
        // questionnaire adoption detail for it instead — the same slug-specific
        // mechanism `summary` already uses.
        slug: "questionnaires",
        label: "Questionnaires",
        owner: "#369",
        projection: "none",
      },
      {
        // #867. LAST IN THE INTERVIEW GROUP, which is exactly where the S7
        // screen contract puts it: "Project entry after Questionnaires, before
        // Summary". Summary is the next GROUP, so the end of Interview is that
        // position — and it is also the runtime order, since the closing agent
        // runs after every final track and before the session ends.
        //
        // `projection: "none"`, like `questionnaires`: `closing` is not a member
        // of the contract's own `SectionSlug`, and `SectionSurface`'s guard
        // exists to stop a rail slug reaching `getProjectSection` on the
        // strength of its name. The body is #867's Closing conversation entry,
        // assembled from the project's pin set and the served placement policy.
        slug: "closing",
        label: "Closing conversation",
        owner: "#867",
        projection: "none",
      },
    ],
  },
  {
    // #153: "Clinical summary content", not "Summary structure" — the rail
    // names only summary prompts/content owned by liv-clinical-packs. Summary
    // layout, identity fields, and redaction policy are Tenant Manifest domain
    // and have no surface here.
    label: "Summary",
    items: [
      { slug: "summary", label: "Clinical summary content", owner: "#36" },
    ],
  },
];

/**
 * The library rail: ONE group, unlabelled on screen, with an icon and a
 * description per entry (#908).
 *
 * The reference's library rail is a flat list of seven icon entries with no
 * group heading above them, and the clinical-configuration rail above IS
 * grouped — `BACKGROUND`, `INTERVIEW`, `SUMMARY`, each a collapsible heading.
 * The difference is real rather than an inconsistency to normalise away, so the
 * two rails declare it: `SectionRail`'s `surface` prop hides this group's
 * heading (`SidebarNav` does exactly the same for the primary navigation's own
 * groups, and for the same reason) while the clinical rail keeps #489's
 * disclosures.
 *
 * THE DESCRIPTIONS ARE THE SECTIONS', NOT THE LIBRARY'S. A single banner over
 * the whole of Clinical library used to carry the one sentence every section
 * shared; each entry now says what its own family holds, which is what the
 * reference shows under each section's heading. None of them names a pack path,
 * a part key or an engine document: the bounded scope keeps that evidence
 * behind Details rather than in navigation copy.
 */
export const LIBRARY_SECTIONS: SectionGroup[] = [
  {
    label: "Library",
    items: [
      // #492: `chief-complaint` is the canonical address, and the spelling the
      // clinical rail above has always used. The LABEL is unchanged — the
      // product name for this family is still "Main concern".
      {
        slug: "chief-complaint",
        label: "Main concern",
        owner: "#33",
        icon: FileText,
        description:
          "Opening templates that shape how a session begins. Library assets are canonical and shared across projects.",
      },
      {
        slug: "pathways",
        label: "Pathways",
        owner: "#33",
        icon: GitBranch,
        description:
          "Parent conditions and their clinical variants. A project pins exact versions, so editing a library asset never changes an adopted project silently.",
      },
      {
        slug: "rules",
        label: "Rules",
        owner: "#33",
        icon: Spline,
        description:
          "Language and conversation rules a session follows. Every rules asset carries each supported language.",
      },
      {
        slug: "safety-paths",
        label: "Safety paths",
        owner: "#35",
        icon: Shield,
        description:
          "Protected safety tracks and the alert matrices that route what they raise. The safety floor stays locked for every project.",
      },
      {
        slug: "bg-sets",
        label: "BG sets",
        owner: "#33",
        icon: ListTree,
        description:
          "Life-history question sets asked at intake, before the clinical interview begins. Adopted by projects from this shared library.",
      },
      {
        slug: "questionnaires",
        label: "Questionnaires",
        owner: "#34",
        icon: ClipboardList,
        description:
          "The approved instrument inventory. Projects pin an exact version of an instrument they are licensed to run.",
      },
      {
        // #866. The Closing conversation is its own group rather than a member
        // of Summary or Safety paths, because it is neither: it is the terminal
        // agent the pinned engine runs AFTER every final track and before the
        // session ends, and the S7 screen contract asks for a separate group in
        // the library for exactly that reason.
        //
        // `projection: "none"`, like `questionnaires`: `closing` is not a member
        // of the contract's own `SectionSlug`, and `SectionSurface`'s guard
        // exists to stop a rail slug reaching `getProjectSection` on the
        // strength of its name. This entry is a LIBRARY destination; the
        // Project-side entry (after Questionnaires, before Summary) and the
        // named step editor are #867's.
        slug: "closing",
        label: "Closing conversation",
        owner: "#866",
        icon: MessageSquareX,
        projection: "none",
        description:
          "The closing message a session ends on, asked once after the final tracks. A project pins one exact version, or none at all.",
      },
      {
        slug: "summary",
        label: "Summary",
        owner: "#36",
        icon: Layers,
        description:
          "Named prompt sections that shape how each part of a session is summarised.",
      },
    ],
  },
];

export function allSectionSlugs(groups: SectionGroup[]): string[] {
  return groups.flatMap((group) => group.items.map((item) => item.slug));
}

/** Whether this entry's body is the contract's section projection (#489). */
export function isProjectionBacked(item: SectionItem): boolean {
  return (item.projection ?? "section") === "section";
}

/**
 * The rail slugs that ARE fetched from `getProjectSection`.
 *
 * The set `src/invariants/routes.test.ts` holds against the contract's
 * `SectionSlug`. Entries declaring `projection: "none"` are excluded here and
 * asserted separately to be absent from that union, so a destination like
 * `questionnaires` can live in the rail without either widening the contract or
 * weakening the guard.
 */
export function projectionBackedSlugs(groups: SectionGroup[]): string[] {
  return groups.flatMap((group) =>
    group.items.filter(isProjectionBacked).map((item) => item.slug),
  );
}

export function findSection(
  groups: SectionGroup[],
  slug: string,
): SectionItem | undefined {
  for (const group of groups) {
    const match = group.items.find((item) => item.slug === slug);
    if (match) {
      return match;
    }
  }
  return undefined;
}
